import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Clock, User, X, Star, Check } from 'lucide-react';
import { Button } from '../../components/button';
import { Input } from '../../components/input';
import { Label } from '../../components';
import CivicPointsLogo from '../../assets/icons/civicpoints_logo.svg';
import IconBrowser from '../../assets/icons/icon-browser.svg';
import type { PublicEventItem } from './types/event.types';
import dayjs from 'dayjs';
import StaticMap from './components/StaticMap';
import axios from 'axios';
import { BASE_API_URL, BASE_URL, REACT_APP_ENV } from '../../config/runtime';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/dialog';
import RegistrationResultModal from './components/RegistrationResultModal';
import WebRegistrationFormModal from './components/WebRegistrationFormModal';
import { formatEventDate, formatEventTime, formatFullEventDateTime } from './utils/event.utils';

// Decode JWT token to get event_id
const decodeJWT = (token: string): string | null => {
    try {
        // JWT format: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        // Decode payload (second part)
        const payload = parts[1];
        // Base64URL decode
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        const decoded = JSON.parse(jsonPayload);
        return decoded.event_id || null;
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
};

const EventPublicDetailsPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const [event, setEvent] = useState<PublicEventItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [showWebRegisterForm, setShowWebRegisterForm] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: ''
    });
    const [registering, setRegistering] = useState(false);
    const [registrationResult, setRegistrationResult] = useState<{
        type: 'success' | 'already_registered' | 'closed' | null;
        message: string;
        userMessage: string;
        email?: string;
    } | null>(null);
    const [showInitialModal, setShowInitialModal] = useState(false);

    useEffect(() => {
        const fetchEvent = async () => {
            if (!token) {
                setError('Token is required');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                
                // Decode JWT token to get event_id
                let eventId = token;
                const decodedEventId = decodeJWT(token);
                if (decodedEventId) {
                    eventId = decodedEventId;
                }
                
                // Call anonymous API to get event details using BASE_URL
                const response = await axios.post(`${BASE_API_URL}/v1/event`, {
                    event_id: eventId
                }, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    }
                });

                // Handle response structure
                let eventData: PublicEventItem | null = null;
                if (response.data) {
                    // Check if response has nested data structure
                    if (response.data.data) {
                        eventData = response.data.data;
                    } else if (response.data.id) {
                        // Direct event object
                        eventData = response.data;
                    }
                }

                if (eventData) {
                    // Transform API response to match PublicEventItem interface
                    const transformedEvent: PublicEventItem = {
                        ...eventData,
                        neighborhood_id: eventData.organization_population_id || null,
                        category_ids: eventData.categories?.map((c: any) => c.id) || [],
                        sub_category_ids: eventData.interests?.map((i: any) => i.id) || [],
                    };
                    setEvent(transformedEvent);
                    // Show initial modal after event is loaded
                    setShowInitialModal(true);
                } else {
                    setError('Event not found');
                }
            } catch (err: any) {
                console.error('Error fetching event:', err);
                setError(err.message || 'Failed to load event');
            } finally {
                setLoading(false);
            }
        };

        fetchEvent();
    }, [token]);

    const handleDownloadApp = () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);
        
        if (isIOS) {
            window.open('https://apps.apple.com/vn/app/civicpoints/id6747657300', '_blank');
        } else if (isAndroid) {
            window.open('https://play.google.com/store/apps/details?id=com.idlemindtech.cityeg.prod', '_blank');
        } else {
            // Show both options
            window.open('https://apps.apple.com/vn/app/civicpoints/id6747657300', '_blank');
        }
    };

    const handleRegister = () => {
        // Show register modal
        setShowRegisterModal(true);
    };

    // const handleContinueInApp = () => {
    //     if (event) {
    //         const eventUrl = `${BASE_URL}/event/${event.id}`;
    //         window.open(eventUrl, '_blank');
    //     }
    //     setShowRegisterModal(false);
    // };

    const handleContinueOnWeb = () => {
        setShowRegisterModal(false);
        setShowWebRegisterForm(true);
    };

    // ---- CONFIG (edit to match your app) ----
    const IOS_STORE_URL = 'https://apps.apple.com/app/civicpoints/id6747657300';
    const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.idlemindtech.cityeg.prod&pcampaignid=web_share';

    // Domain for Universal Links
    const UNIVERSAL_BASE =
    process.env.REACT_APP_ENV === 'dev'
        ? 'https://dev-admin.civicpoints.io'
        : 'https://admin.civicpoints.io'; // <- change if your prod domain differs

    // App URL scheme
    const APP_SCHEME = process.env.REACT_APP_ENV === 'dev' ? 'civicapp-dev' : 'civicapp';

    // Android package (dev/prod). Update if your dev package differs.
    const ANDROID_PACKAGE =
    process.env.REACT_APP_ENV === 'dev'
        ? 'com.idlemindtech.cityeg.dev'
        : 'com.idlemindtech.cityeg.prod';

    const handleOpenEventInApp = () => {
         if (!event?.id) return;

        const id = encodeURIComponent(event.id);
        const ua = navigator.userAgent || '';

        const isIOS = /iPhone|iPad|iPod/i.test(ua);
        const isAndroid = /Android/i.test(ua);
        const isSafari = isIOS && /Safari/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
        const isInApp = /(FBAN|FBAV|Instagram|Line|Zalo|Zbrowser|TikTok|KAKAOTALK|WeChat|Twitter)/i.test(ua);

        const universalLink = `${UNIVERSAL_BASE}/event/${id}`;
        const schemeLink = `${APP_SCHEME}://event/${id}`;
        const androidIntent =
            `intent://event/${id}#Intent;` +
            `scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};` +
            `S.browser_fallback_url=${encodeURIComponent(universalLink)};end`;

        // If not mobile, show dialog and just open web
        if (!(isIOS || isAndroid)) {
            window.location.assign(universalLink);
            return;
        }

        let timer: number | undefined;
        let cancelled = false;

        const onVisibility = () => {
            // If app opens, page becomes hidden -> cancel fallback
            if (document.hidden) {
            cancelled = true;
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisibility);
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        const toStoreOrWeb = () => {
            if (cancelled) return;
            document.removeEventListener('visibilitychange', onVisibility);

            if (isIOS) {
            // prefer Store; change to universalLink if you want to stay on web
            window.location.assign(IOS_STORE_URL);
            } else if (isAndroid) {
            window.location.assign(ANDROID_STORE_URL);
            } else {
            window.location.assign(universalLink);
            }
        };

        // Strategy by platform/browser:
        if (isAndroid && !isInApp) {
            // Best on Android Chrome
            window.location.assign(androidIntent);
            timer = window.setTimeout(toStoreOrWeb, 1200);
        } else if (isIOS && isSafari) {
            // Best on Safari iOS: Universal Link first (avoids "address is invalid")
            window.location.assign(schemeLink);
            timer = window.setTimeout(() => {
            if (!cancelled) window.location.assign(universalLink);
            timer = window.setTimeout(toStoreOrWeb, 1200);
            }, 1200);
        } else if (isIOS && !isSafari) {
            // iOS in-app browsers: try scheme then UL then fallback
            window.location.assign(schemeLink);
            timer = window.setTimeout(() => {
            if (!cancelled) window.location.assign(universalLink);
            timer = window.setTimeout(toStoreOrWeb, 1200);
            }, 1200);
        } else if (isAndroid && isInApp) {
            // Android in-app browsers: scheme then UL then fallback
            window.location.assign(schemeLink);
            timer = window.setTimeout(() => {
            if (!cancelled) window.location.assign(universalLink);
            timer = window.setTimeout(toStoreOrWeb, 1000);
            }, 800);
        } else {
            // Fallback
            window.location.assign(universalLink);
            timer = window.setTimeout(toStoreOrWeb, 1500);
        }

        setShowInitialModal(false);
    };

    const handleContinueInBrowser = () => {
        setShowInitialModal(false);
    };

    const handleWebRegister = async () => {
        if (!event || !token) return;

        setRegistering(true);
        try {
            const payload = {
                event_id: event.id,
                name: `${formData.firstName} ${formData.lastName}`,
                email: formData.email,
                phone: formData.phone || '',
                qr_token: token // Use the token from query param (JWT token)
            };

            const response = await axios.post(
                `${BASE_API_URL}/v1/event/guest-user-register-event`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    }
                }
            );

            // Handle success response (201)
            if (response.data.success && response.data.http_request_status === 201) {
                setShowWebRegisterForm(false);
                setRegistrationResult({
                    type: 'success',
                    message: response.data.message,
                    userMessage: response.data.data?.message || response.data.user_message,
                    email: formData.email
                });
            } 
            // Handle already registered (409)
            else if (!response.data.success && response.data.http_request_status === 409) {
                setShowWebRegisterForm(false);
                setRegistrationResult({
                    type: 'already_registered',
                    message: response.data.message,
                    userMessage: response.data.user_message,
                    email: formData.email
                });
            }
            // Handle registration closed (400)
            else if (!response.data.success && response.data.http_request_status === 400) {
                setShowWebRegisterForm(false);
                setRegistrationResult({
                    type: 'closed',
                    message: response.data.message,
                    userMessage: response.data.user_message
                });
            }
            // Other cases
            else {
                alert(response.data.message || 'Registration failed. Please try again.');
            }
            
            // Reset form
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: ''
            });
        } catch (error: any) {
            console.error('Registration error:', error);
            
            // Check if it's a registration closed error (400)
            if (error.response?.status === 400 && error.response?.data?.message === 'Registration Closed') {
                setShowWebRegisterForm(false);
                setRegistrationResult({
                    type: 'closed',
                    message: error.response.data.message,
                    userMessage: error.response.data.user_message
                });
            } 
            // Check if it's already registered (409)
            else if (error.response?.status === 409) {
                setShowWebRegisterForm(false);
                setRegistrationResult({
                    type: 'already_registered',
                    message: error.response.data.message,
                    userMessage: error.response.data.user_message,
                    email: formData.email
                });
            } else {
                // Other errors
                alert(error.response?.data?.message || 'Registration failed. Please try again.');
            }
        } finally {
            setRegistering(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading event...</p>
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-4">
                <div className="text-center">
                    <p className="text-red-600 mb-4">{error || 'Event not found'}</p>
                    <Button onClick={() => navigate('/')}>Go Home</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <div className=" px-4 py-3">
                <div className="flex items-center justify-between max-w-4xl mx-auto">
                    <div className="flex items-center">
                        <img src={CivicPointsLogo} alt="CivicPoints" className="h-5" />
                    </div>
                    <button
                        onClick={handleDownloadApp}
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    >
                        <span>Download the app</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.66089 4.66083H11.3192V11.3192" stroke="#999999" strokeWidth="1.33167" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M4.66089 11.3192L11.3192 4.66083" stroke="#999999" strokeWidth="1.33167" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Event Content */}
            <div className="max-w-4xl mx-auto px-4 pb-5 md:pb-6">
                {/* Event Image */}
                {event.image_url && (
                    <div className="mb-6 -mx-4 md:-mx-4" style={{ aspectRatio: '390.449951171875 / 204' }}>
                        <img
                            src={event.image_url}
                            alt={event.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}

                {/* Date and Time */}
                <div className="mb-2">
                    <p className="text-sm text-gray-500">
                        {formatEventDate(event.event_date, event.time_zone)}, {formatEventTime(event.start_time, event.time_zone)}
                    </p>
                </div>

                {/* Event Title */}
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                    {event.title}
                </h1>

                {/* Location */}
                <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <span className="text-gray-700">{event.location || 'Location TBD'}</span>
                </div>

                {/* Organizer */}
                <div className="flex items-center justify-between mb-6 pb-6  ">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                            <p className="font-medium text-gray-900">City of Fairburn</p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            const recipientEmail = 'support.civicpoints@gmail.com';
                            const subject = 'Support event'
                            
                            // Detect if mobile device
                            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                            
                            if (isMobile) {
                                const mailtoLink = `mailto:${recipientEmail}?subject=${encodeURIComponent(subject)}`;
                                const attempted = window.open(mailtoLink);
                                setTimeout(() => {
                                    // If window.open returned null or was closed immediately, try Gmail web
                                    if (!attempted || attempted.closed) {
                                        const gmailComposeUrl = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${encodeURIComponent(recipientEmail)}&su=${encodeURIComponent(subject)}`;
                                        window.open(gmailComposeUrl, '_blank');
                                    }
                                }, 500);
                            } else {
                                const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipientEmail)}&su=${encodeURIComponent(subject)}`;
                                window.open(gmailComposeUrl, '_blank');
                            }
                        }}
                        className="border border-[#004231] text-[#004231] rounded-[59px] px-[14px] py-[5px] w-[74px] h-[31px] flex items-center justify-center text-sm font-medium hover:bg-[#004231] hover:text-white transition-colors"
                    >
                        Contact
                    </button>
                </div>

                {/* About Event */}
                <div className="mb-6 pb-6  ">
                    <h2 className="text-xl font-bold text-gray-900 mb-3">About Event</h2>
                    <p className="text-gray-700 leading-relaxed">
                        {event.description || 'No description available.'}
                    </p>
                </div>

                {/* Location Section */}
                <div className="mb-6 ">
                    <h2 className="text-xl font-bold text-gray-900 mb-3">Location</h2>
                    <div className="mb-4">
                        <p className="font-semibold text-gray-900 mb-1">
                            {event.location || 'Location TBD'}
                        </p>
                    </div>
                </div>

                {/* Map */}
                {event.latitude && event.longitude && (
                    <StaticMap
                        latitude={event.latitude}
                        longitude={event.longitude}
                        location={event.location || 'Event Location'}
                        height="256px"
                        className="rounded-lg"
                    />
                )}
            </div>

            {/* Footer with Download App Bar - Above Register Button */}
            <div className="flex justify-center pb-20 md:pb-3">
                <div 
                    className="w-full h-[91px] px-4 py-5 flex items-center justify-between border-t"
                    style={{ 
                        backgroundColor: 'hsla(125, 48%, 95%, 1)',
                        borderTopWidth: '1.49px',
                        borderTopColor: '#e5e7eb'
                    }}
                >
                    <div className="flex items-center">
                        <img src={CivicPointsLogo} alt="CivicPoints" className="h-5" />
                    </div>
                    <button
                        onClick={handleDownloadApp}
                        className="flex items-center gap-[10px] text-sm text-gray-500 hover:text-gray-700"
                    >
                        <span>Download the app</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.66089 4.66083H11.3192V11.3192" stroke="#999999" strokeWidth="1.33167" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M4.66089 11.3192L11.3192 4.66083" stroke="#999999" strokeWidth="1.33167" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Register Button - Fixed on mobile, relative on desktop */}
            <div  className="w-full fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-center z-[99]">
                <button
                    onClick={handleRegister}
                    className="w-full md:w-[350px] h-[44px] px-2 py-auto rounded-[33px] font-semibold text-lg shadow-lg hover:opacity-90 transition-opacity"
                    style={{ 
                        backgroundColor: 'hsla(167, 100%, 42%, 1)',
                        color: 'hsla(165, 100%, 13%, 1)'
                    }}
                >
                    Register
                </button>
            </div>

            {/* Initial Modal - Bottom Sheet (Shown on page load) */}
            <Dialog open={showInitialModal} onOpenChange={setShowInitialModal}>
                <DialogContent 
                    className="fixed bottom-0 left-0 right-0 top-auto translate-y-0 translate-x-0 max-w-full w-full rounded-t-3xl rounded-b-none p-0 mb-0"
                    dialogOverlayClassName="bg-black/50"
                    style={{ zIndex: 100 }}
                >
                    <DialogTitle className="sr-only">See CivicPoints in...</DialogTitle>
                    <div className="p-6 pb-8">
                        {/* Drag Handle */}
                        <div className="flex justify-center mb-4">
                            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                            See CivicPoints in...
                        </h3>

                        {/* CivicPoints App Option */}
                        <div className="mb-4">
                            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                                        <img src="/favicon.ico" alt="CivicPoints" className="w-full h-full object-contain" />
                                    </div>
                                    <span className="text-gray-900 font-medium">CivicPoints App</span>
                                </div>
                                <button
                                    onClick={handleOpenEventInApp}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                                    style={{ width: '100px', minWidth: '100px' }}
                                >
                                    Open
                                </button>
                            </div>
                        </div>

                        {/* Browser Option */}
                        <div>
                            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden">
                                        <img src={IconBrowser} alt="Browser" className="w-full h-full object-contain" />
                                    </div>
                                    <span className="text-gray-900 font-medium">Browser</span>
                                </div>
                                <button
                                    onClick={handleContinueInBrowser}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full font-medium hover:bg-gray-300 transition-colors flex items-center justify-center"
                                    style={{ width: '100px', minWidth: '100px' }}
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Register Modal - Bottom Sheet */}
            <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
                <DialogContent 
                    className="fixed bottom-0 left-0 right-0 top-auto translate-y-0 translate-x-0 max-w-full w-full rounded-t-3xl rounded-b-none p-0 mb-0"
                    dialogOverlayClassName="bg-black/50"
                    style={{ zIndex: 100 }}
                >
                    <DialogTitle className="sr-only">We found your CivicPoints account!</DialogTitle>
                    <div className="p-6">
                        {/* Account Found Section */}
                        <div className="mb-4 text-center">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                We found your CivicPoints account!
                            </h3>
                            <p className="text-sm text-gray-600">
                                Please open the app to complete your registration so it syncs with your profile and you can earn points.
                            </p>
                        </div>

                        {/* Benefits Section */}
                        <div className="bg-[#E8F5E9] rounded-lg p-4 space-y-3 mb-4">
                            <div className="flex items-start gap-3">
                                <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="#FEC057" />
                                <span className="text-sm text-gray-700">Earn points for attending this event</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="#FEC057" />
                                <span className="text-sm text-gray-700">All registrations in one place</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="#FEC057" />
                                <span className="text-sm text-gray-700">QR code for easy check-in</span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3">
                            <button
                                onClick={handleOpenEventInApp}
                                className="w-full h-[44px] rounded-[33px] text-black font-semibold text-base shadow-lg hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: 'hsla(167, 100%, 42%, 1)' }}
                            >
                                Continue registration in app
                            </button>
                            <button
                                onClick={handleContinueOnWeb}
                                className="w-full h-[44px] rounded-[33px] border border-gray-300 text-gray-700 font-semibold text-base hover:bg-gray-50 transition-colors"
                            >
                                Continue on web
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Web Registration Form Modal */}
            <WebRegistrationFormModal
                open={showWebRegisterForm}
                onClose={() => setShowWebRegisterForm(false)}
                event={event}
                formData={formData}
                onFormDataChange={setFormData}
                onRegister={handleWebRegister}
                registering={registering}
                formatFullDateTime={formatFullEventDateTime}
            />

            {/* Registration Result Modal */}
            <RegistrationResultModal
                registrationResult={registrationResult}
                onClose={() => setRegistrationResult(null)}
                event={event}
                onDownloadApp={handleDownloadApp}
                formatFullDateTime={formatFullEventDateTime}
            />
        </div>
    );
};

export default EventPublicDetailsPage;

